import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch } from "./api";
import elumeLogo from "./assets/ELogo2.png";
import LanguageSwitch from "./Components/LanguageSwitch";
import { useUiLanguage } from "./i18n/UiLanguageContext";

function passwordPolicyError(password: string, t: (key: string) => string) {
    if (password.length < 8) return t("register.passwordMinimum");
    if (!/[A-Z]/.test(password)) return t("register.passwordUppercase");
    if (!/[a-z]/.test(password)) return t("register.passwordLowercase");
    if (!/[0-9]/.test(password)) return t("register.passwordNumber");
    return null;
}

export default function ResetPasswordPage() {
    const { t } = useUiLanguage();
    const [params] = useSearchParams();
    const token = useMemo(() => (params.get("token") || "").trim(), [params]);
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    async function submit(e: React.FormEvent) {
        e.preventDefault();
        setError(null);
        setSuccess(null);

        if (!token) {
            setError(t("resetPassword.invalid"));
            return;
        }
        const passwordError = passwordPolicyError(newPassword, t);
        if (passwordError) {
            setError(passwordError);
            return;
        }
        if (newPassword !== confirmPassword) {
            setError(t("register.passwordMismatch"));
            return;
        }

        setLoading(true);
        try {
            const data = await apiFetch("/auth/reset-password", {
                method: "POST",
                body: JSON.stringify({
                    token,
                    new_password: newPassword,
                }),
            });
            setSuccess(data?.message || t("resetPassword.success"));
            setNewPassword("");
            setConfirmPassword("");
        } catch (err: any) {
            setError(err?.message || t("resetPassword.failure"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-emerald-50">
            <div className="absolute right-4 top-4 z-20"><LanguageSwitch /></div>
            <div className="pointer-events-none absolute inset-0 overflow-hidden">
                <div className="absolute -left-24 top-[-60px] h-80 w-80 rounded-full bg-cyan-300/25 blur-3xl" />
                <div className="absolute right-[-80px] top-24 h-96 w-96 rounded-full bg-emerald-300/20 blur-3xl" />
            </div>

            <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10">
                <div className="w-full max-w-md rounded-[32px] border border-white/70 bg-white/90 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.10)] backdrop-blur-xl sm:p-7">
                    <div className="mb-6 flex items-center gap-4">
                        <div className="grid h-16 w-16 place-items-center rounded-3xl border border-white/70 bg-white/80 shadow-xl ring-1 ring-emerald-100 backdrop-blur">
                            <img src={elumeLogo} alt="Elume" className="h-12 w-12 object-contain" />
                        </div>
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-emerald-700">
                                {t("resetPassword.recovery")}
                            </div>
                            <h1 className="mt-1 text-3xl font-black tracking-tight text-slate-900">
                                {t("resetPassword.title")}
                            </h1>
                        </div>
                    </div>

                    <p className="mb-5 text-sm leading-6 text-slate-600">
                        {t("resetPassword.help")}
                    </p>

                    <form className="space-y-4" onSubmit={submit}>
                        <label className="block">
                            <span className="mb-1.5 block text-sm font-bold text-slate-800">
                                {t("resetPassword.new")}
                            </span>
                            <input
                                type="password"
                                className="w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                required
                            />
                        </label>

                        <label className="block">
                            <span className="mb-1.5 block text-sm font-bold text-slate-800">
                                {t("resetPassword.confirm")}
                            </span>
                            <input
                                type="password"
                                className="w-full rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                autoComplete="new-password"
                                minLength={8}
                                required
                            />
                        </label>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs leading-6 text-slate-600">
                            {t("register.passwordHelp")}
                        </div>

                        {error && (
                            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-medium text-emerald-700">
                                {success}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={loading || !token}
                            className="w-full rounded-2xl bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 px-5 py-3 text-base font-black text-white shadow-lg transition hover:shadow-xl disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {loading ? t("resetPassword.loading") : t("resetPassword.submit")}
                        </button>
                    </form>

                    <div className="mt-5 text-center text-sm text-slate-600">
                        <Link to="/" className="font-semibold text-emerald-700 transition hover:text-emerald-800 hover:underline">
                            {t("resetPassword.back")}
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
